import FlipIcon from '@mui/icons-material/Flip'
import {Button, Stack, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material'
import {useSnackbar} from 'notistack'
import {Dispatch, SetStateAction} from 'react'
import {useTranslation} from 'react-i18next'

import {getConnectedSignals, useConnectedSignals} from '../../../hooks'
import {useKilnConfig} from '../../../providers'
import {KilnConfigActionType, type SignalsModalData} from '../../../types'
import {KilnConfigTextField, KilnPartDimensionsInputs} from '../common'
import {ConnectSignalsButton} from '../ConnectSignalsButton'

interface KilnDriveConfigInputsProps {
  disabledSignals: string[]
  setSignalsModalData: Dispatch<SetStateAction<SignalsModalData | undefined>>
  setSignalsDisconnectWarning: Dispatch<
    SetStateAction<{
      open: boolean
      signals: string[]
      action: (() => void) | null
    }>
  >
}

export const KilnDriveConfigInputs: React.FC<KilnDriveConfigInputsProps> = ({
  disabledSignals,
  setSignalsModalData,
  setSignalsDisconnectWarning
}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()

  const {kilnLayout, dispatch} = useKilnConfig()

  const connectedSignals = useConnectedSignals({
    selectedSignals: kilnLayout.selectedSignals,
    kilnPartId: kilnLayout.drive.id
  })

  const handleToggle = (newDriveType: 'single' | 'dual') => {
    if (newDriveType === 'dual') {
      dispatch({type: KilnConfigActionType.SET_DUAL_DRIVE})
      return
    }

    const dispatchActions = () => {
      if (newDriveType === 'single' && kilnLayout.isRotationClockwise) {
        dispatch({type: KilnConfigActionType.SET_BOTTOM_DRIVE})
      } else {
        dispatch({type: KilnConfigActionType.SET_TOP_DRIVE})
      }
    }

    const connectedSignals = getConnectedSignals(kilnLayout.selectedSignals, kilnLayout.drive.id)

    if (connectedSignals.length > 0) {
      setSignalsDisconnectWarning({
        open: true,
        signals: connectedSignals,
        action: () => {
          dispatchActions()
          enqueueSnackbar(
            `Drive layout changed. Following signals were disconnected: ${connectedSignals.join(
              ', '
            )}`,
            {
              variant: 'warning'
            }
          )
        }
      })
    } else {
      dispatchActions()
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h3">{t('kilnConfig.driveDetails')}</Typography>
      <Stack direction="row" spacing={1.5}>
        <KilnConfigTextField
          label="Name"
          data-test-id="drive-name-input"
          value={kilnLayout.drive.name ?? ''}
          onChange={(e) =>
            dispatch({
              type: KilnConfigActionType.SET_DRIVE_NAME,
              payload: e.target.value
            })
          }
        />
        <ConnectSignalsButton
          connectedSignals={connectedSignals.length}
          onClick={() =>
            setSignalsModalData({
              type: 'drive',
              layout: {
                ...kilnLayout.drive,
                flip:
                  kilnLayout.drive.side === 'right' ||
                  (!kilnLayout.drive.side && kilnLayout.materialFlow !== 'rightToLeft')
              },
              disabledSignals: disabledSignals
            })
          }
          testId="connect-drive-signal-btn"
        />
      </Stack>
      <Stack direction="row" spacing={1.5}>
        <ToggleButtonGroup
          value={kilnLayout.drive.position === 'double' ? 'dual' : 'single'}
          exclusive
          onChange={(e, newDriveType: 'single' | 'dual') =>
            newDriveType && handleToggle(newDriveType)
          }
          color="primary"
          sx={{
            height: '48px',
            background: '#F0F2F5',
            border: '1px solid #E8ECF0',
            '& .MuiToggleButtonGroup-grouped': {border: 'none !important'}
          }}
        >
          <ToggleButton
            fullWidth
            value="single"
            sx={{border: 'none'}}
            data-test-id="drive-type-single-btn"
          >
            <Typography>{t('kilnConfig.signleDrive')}</Typography>
          </ToggleButton>
          <ToggleButton
            fullWidth
            value="dual"
            sx={{border: 'none'}}
            data-test-id="drive-type-dual-btn"
          >
            <Typography>{t('kilnConfig.dualDrive')}</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<FlipIcon />}
          sx={{border: '1px solid #E8ECF0'}}
          onClick={() => dispatch({type: KilnConfigActionType.FLIP_MOTOR})}
          data-test-id="drive-flip-btn"
        >
          {t('kilnConfig.flipDrive')}
        </Button>
      </Stack>
      <KilnPartDimensionsInputs selectedKilnPart={kilnLayout.drive} />
    </Stack>
  )
}
